import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import AdminSubNav from "../components/AdminSubNav";
import { useAdminAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { History, ExternalLink } from "lucide-react";

function formatDate(iso) {
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
}

function formatSize(bytes) {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

const ACTION_LABEL = {
    "artwork.upload": "Artwork uploaded",
};

export default function AdminAudit() {
    const { admin } = useAdminAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [outbound, setOutbound] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const [auditRes, outboundRes] = await Promise.all([
                    api.get("/admin/audit-log"),
                    api.get("/admin/outbound-stats"),
                ]);
                setItems(auditRes.data.items || []);
                setOutbound(outboundRes.data?.stats || null);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    if (admin === null) return null;
    if (!admin) return <Navigate to="/login" replace />;

    return (
        <PortalLayout>
            <section className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 pt-16 pb-32" data-testid="admin-audit-page">
                <div className="mb-10">
                    <span className="font-label text-[11px] text-[#cfa052]">Admin · audit</span>
                    <h1 className="mt-4 font-serif-display text-5xl md:text-6xl tracking-tighter text-[#e8e4db]" data-testid="admin-audit-title">
                        Curator <span className="italic">history</span>
                    </h1>
                    <p className="mt-4 font-poetic text-[#9a958a] max-w-2xl">
                        Every artwork change, who made it, and when. Latest first.
                    </p>
                </div>

                <AdminSubNav />

                {outbound && Object.keys(outbound).length > 0 && (
                    <div className="mb-12" data-testid="admin-outbound-stats">
                        <div className="flex items-center gap-2 mb-4">
                            <ExternalLink className="w-4 h-4 text-[#cfa052]" />
                            <span className="font-label text-[10px] tracking-[0.25em] uppercase text-[#cfa052]">
                                Member outbound
                            </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {Object.entries(outbound).map(([dest, s]) => (
                                <React.Fragment key={dest}>
                                    <div className="rounded border border-white/10 bg-white/[0.02] p-5" data-testid={`admin-outbound-${dest}-week`}>
                                        <div className="font-label text-[10px] tracking-[0.25em] uppercase text-[#9a958a] mb-2">
                                            {dest}.com · 7 days
                                        </div>
                                        <div className="font-numerals text-3xl text-[#e8e4db]">{s.week_clicks}</div>
                                        <div className="font-label text-[10px] text-[#6e6a62] mt-2">
                                            {s.week_unique_members} unique member{s.week_unique_members === 1 ? "" : "s"}
                                        </div>
                                    </div>
                                    <div className="rounded border border-white/10 bg-white/[0.02] p-5" data-testid={`admin-outbound-${dest}-month`}>
                                        <div className="font-label text-[10px] tracking-[0.25em] uppercase text-[#9a958a] mb-2">
                                            {dest}.com · 30 days
                                        </div>
                                        <div className="font-numerals text-3xl text-[#e8e4db]">{s.month_clicks}</div>
                                        <div className="font-label text-[10px] text-[#6e6a62] mt-2">
                                            {s.month_unique_members} unique member{s.month_unique_members === 1 ? "" : "s"}
                                        </div>
                                    </div>
                                    <div className="rounded border border-white/10 bg-white/[0.02] p-5" data-testid={`admin-outbound-${dest}-total`}>
                                        <div className="font-label text-[10px] tracking-[0.25em] uppercase text-[#9a958a] mb-2">
                                            {dest}.com · all-time
                                        </div>
                                        <div className="font-numerals text-3xl text-[#cfa052]">{s.total}</div>
                                        <div className="font-label text-[10px] text-[#6e6a62] mt-2">
                                            clicks since launch
                                        </div>
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="text-center py-32 font-poetic text-[#9a958a]" data-testid="admin-audit-loading">turning the pages…</div>
                ) : items.length === 0 ? (
                    <div className="text-center py-32" data-testid="admin-audit-empty">
                        <History className="w-8 h-8 mx-auto text-[#6e6a62] mb-3" />
                        <p className="font-poetic text-[#9a958a]">No curator changes recorded yet.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto" data-testid="admin-audit-table-wrap">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-white/10">
                                    <th className="font-label text-[10px] text-[#cfa052] py-3 pr-6">When</th>
                                    <th className="font-label text-[10px] text-[#cfa052] py-3 pr-6">Action</th>
                                    <th className="font-label text-[10px] text-[#cfa052] py-3 pr-6">Angel</th>
                                    <th className="font-label text-[10px] text-[#cfa052] py-3 pr-6">By</th>
                                    <th className="font-label text-[10px] text-[#cfa052] py-3 pr-6">Type</th>
                                    <th className="font-label text-[10px] text-[#cfa052] py-3 pr-6">Size</th>
                                    <th className="font-label text-[10px] text-[#cfa052] py-3">Version</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5 font-poetic" data-testid="admin-audit-list">
                                {items.map((row) => (
                                    <tr key={row.id} className="text-[#e8e4db]" data-testid={`admin-audit-row-${row.id}`}>
                                        <td className="py-3 pr-6 text-[#9a958a] whitespace-nowrap">{formatDate(row.timestamp)}</td>
                                        <td className="py-3 pr-6">{ACTION_LABEL[row.action] || row.action}</td>
                                        <td className="py-3 pr-6">
                                            <span className="font-numerals text-lg text-[#cfa052]">{row.angelNumber}</span>
                                        </td>
                                        <td className="py-3 pr-6 text-[#9a958a] break-all">{row.adminEmail}</td>
                                        <td className="py-3 pr-6 text-[#6e6a62]">{row.contentType}</td>
                                        <td className="py-3 pr-6 text-[#6e6a62]">{formatSize(row.filesizeBytes)}</td>
                                        <td className="py-3 text-[#6e6a62] font-label text-[10px]">
                                            {row.previousImageVersion || "—"} → {row.newImageVersion}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </PortalLayout>
    );
}
